import { Controller, FieldError, get, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded, WarningAmberRounded } from '@mui/icons-material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Checkbox, IconButton, ListItemText, MenuItem, Tooltip, Typography } from '@mui/material';
import Constants from 'style/constants';
import Divider from 'shared/components/divider/divider';
import SelectField from 'shared/components/formFields/select-field';
import OverflowMenu, { OverflowMenuItem } from 'shared/components/overflow-menu/overflow-menu';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import { stringToStringArray } from 'shared/utils/string-utils';

import { IS32AccordionType } from 'features/systems/IS32/domain/models/IS32-accordion-type';
import { IS32LocalBusUnit } from 'features/systems/IS32/domain/models/IS32-localbus-unit';
import { IS32LocalbusUnitType } from 'features/systems/IS32/domain/models/IS32-localbus-unit-type';
import { IS32Locations } from 'features/systems/IS32/domain/models/IS32-locations';
import { useIS32DetailsContextProvider } from 'features/systems/IS32/providers/IS32-details.provider';
import IS32ConfigurationAccordion from 'features/systems/IS32/views/configuration-components/IS32-configuration-accordion.component';
import styles from 'features/systems/IS32/views/configuration-components/IS32-localbus-units.module.scss';
import IS32LocalbusUnitPOEntry from './localbus-units-components/IS32-localbus-unit-po-entry';

interface Props {
  accordionEnabled: boolean;
}

const IS32LocalbusUnits = ({ accordionEnabled }: Props) => {
  const { t } = useTranslation('IS32');

  const {
    formState: { errors },
    control,
    getValues,
  } = useFormContext();

  const { fields } = useFieldArray({
    name: 'localBusUnits',
    control,
  });

  const registerOptions = {
    location: {
      required: t('configuration.localbusUnitsConfiguration.required'),
    },
  };

  const {
    selectedIS32ConfigurationUnit,
    viewingMode,
    getLocalbusUnitViewingMode,
    changeLocalbusUnitViewingMode,
    resetLocalbusUnitFormValues,
    setLocalbusUnitLocationValue,
    setLocalbusUnitEventsValue,
    deleteLocalbusUnit,
    canDeleteLocalbusUnit,
    revertDeleteLocalbusUnit,
    localbusUnitIsDeleted,
    createIS32Device,
    getDeviceAlertsForLocalbusUnit,
    is32AccordionHook: { localbusUnitsExpanded, handleAccordionExpandedChange },
    is32OverflowMenuHook: { onOverflowMenuClicked, closeOverflowMenu, isOverflowMenuOpen, anchorEl, clickedRow, clickedPOEntryIndex },
  } = useIS32DetailsContextProvider();

  const header = () => <Divider className={styles.divider} startText={t('configuration.localbusUnitsConfiguration.title')} variant="h6" />;

  const getOverflowMenuItems = (rowData: IS32LocalBusUnit, poEntryIndex?: number): Array<OverflowMenuItem> => {
    const menuItems: Array<OverflowMenuItem> = [];

    if (rowData.type === IS32LocalbusUnitType.OP && typeof poEntryIndex === 'number') {
      menuItems.push({
        label: t('configuration.localbusUnitsConfiguration.createDevice'),
        action: () => {
          createIS32Device(poEntryIndex, undefined, rowData.reference);
        },
        startIcon: <AddCircleOutlineRounded />,
        sx: { color: Constants.Colors.primary },
        disabled: false,
      });
    } else if (localbusUnitIsDeleted(rowData.reference)) {
      menuItems.push({
        label: t('configuration.localbusUnitsConfiguration.restore'),
        action: () => {
          revertDeleteLocalbusUnit(rowData.reference);
        },
        disabled: false,
      });
    } else {
      menuItems.push({
        label: t('configuration.localbusUnitsConfiguration.edit'),
        action: () => {
          changeLocalbusUnitViewingMode(rowData.index, 'editing');
        },
        disabled: false,
      });

      if (canDeleteLocalbusUnit(selectedIS32ConfigurationUnit?.address ?? '', rowData.reference)) {
        menuItems.push('divider', {
          label: t('configuration.localbusUnitsConfiguration.delete'),
          action: () => {
            deleteLocalbusUnit(rowData.reference);
          },
          disabled: false,
        });
      }
    }

    return menuItems;
  };

  const renderSelectedOptions = (selected: string[]) => {
    return selected
      .slice()
      .sort((a, b) => {
        const priorityItem = IS32Locations.Room;
        if (a === priorityItem) {
          return b === priorityItem ? 0 : -1;
        }
        if (b === priorityItem) {
          return 1;
        }
        return a.localeCompare(b);
      })
      .map((value) => t(`configuration.localbusUnitsConfiguration.${value}`))
      .join(', ');
  };

  const selectLocation = (item: IS32LocalBusUnit) => {
    const fieldName = `localBusUnits.${item.index}.locations`;
    const fieldId = fields[item.index]?.id;
    const fieldError = get(errors, fieldName) as FieldError | undefined;
    const totalLocationsCount = Object.values(IS32Locations).length;
    const itemViewingMode = getLocalbusUnitViewingMode(item.index);

    if (item.type === IS32LocalbusUnitType.OP) {
      return <></>;
    }

    if (itemViewingMode === 'viewing') {
      const currentFormLocations = getValues(fieldName);

      const locationsToDisplay = stringToStringArray(currentFormLocations);

      if (locationsToDisplay.length === totalLocationsCount) {
        return (
          <Typography
            variant="subtitle1"
            sx={{
              textDecoration: localbusUnitIsDeleted(item.reference) ? 'line-through' : 'none',
            }}
          >
            {t('configuration.localbusUnitsConfiguration.allLocations')}
          </Typography>
        );
      } else {
        return (
          <>
            {locationsToDisplay
              .slice()
              .sort((a, b) => {
                const priorityItem = IS32Locations.Room;
                if (a === priorityItem) {
                  return b === priorityItem ? 0 : -1;
                }
                if (b === priorityItem) {
                  return 1;
                }
                return a.localeCompare(b);
              })
              .map((location) => (
                <Typography
                  key={location}
                  variant="subtitle1"
                  sx={{
                    textDecoration: localbusUnitIsDeleted(item.reference) ? 'line-through' : 'none',
                  }}
                >
                  {t(`configuration.localbusUnitsConfiguration.${location}`)}
                </Typography>
              ))}
          </>
        );
      }
    } else if (viewingMode === 'editing' && itemViewingMode === 'editing') {
      return (
        <Controller
          name={fieldName}
          control={control}
          rules={registerOptions.location}
          render={({ field }) => {
            const multiple = item.type === IS32LocalbusUnitType.KL;

            let valueForSelect: string | string[] = '';
            if (multiple) {
              valueForSelect = stringToStringArray(field.value);
            } else if (Array.isArray(field.value)) {
              valueForSelect = field.value[0] ?? '';
            } else {
              valueForSelect = field.value ?? '';
            }

            return (
              <SelectField
                {...field}
                value={valueForSelect}
                inputProps={{ 'data-testid': 'locationInput' }}
                id={fieldId}
                error={!!fieldError}
                helperText={fieldError?.message?.toString()}
                variant="outlined"
                className={styles.selectField}
                multiple={multiple}
                renderValue={(selected) => {
                  const selectionArray = multiple ? selected : [selected];

                  return renderSelectedOptions(selectionArray as string[]);
                }}
              >
                {Object.values(IS32Locations).map((option) => {
                  if (multiple) {
                    return (
                      <MenuItem key={`option-${option}`} value={option}>
                        <Checkbox sx={{ padding: '0 9px' }} checked={field.value.includes(option)} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    );
                  } else {
                    return (
                      <MenuItem data-testid={`gateway-type-${option}`} key={`gatewayType-${option}`} value={option}>
                        {t(`configuration.localbusUnitsConfiguration.${option}`)}
                      </MenuItem>
                    );
                  }
                })}
              </SelectField>
            );
          }}
        />
      );
    }
    return <></>;
  };

  const localbusUnitEvents = (item: IS32LocalBusUnit) => {
    if (item.type === IS32LocalbusUnitType.OP) {
      const entryIndices = [0, 1];

      return (
        <div className={styles.localbusUnitPOEntry}>
          {entryIndices.map((poEntryIndex) => (
            <IS32LocalbusUnitPOEntry key={`PO_entry_localbus_${poEntryIndex}`} poEntryIndex={poEntryIndex} item={item} />
          ))}
        </div>
      );
    }
    return <></>;
  };

  const actionButtons = (item: IS32LocalBusUnit) => {
    const itemViewingMode = getLocalbusUnitViewingMode(item.index);
    const fieldName = `localBusUnits.${item.index}.locations`;
    const fieldError = get(errors, fieldName) as FieldError | undefined;
    const hideOverflowMenuOptions = item.type === IS32LocalbusUnitType.PD0304 || item.type === IS32LocalbusUnitType.PD0404;

    if (viewingMode === 'editing' && itemViewingMode === 'viewing' && !hideOverflowMenuOptions) {
      return (
        <div className={styles.actionButtons}>
          <IconButton onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => onOverflowMenuClicked(event, item)}>
            <MoreHorizIcon className={styles.moreIcon} />
          </IconButton>
        </div>
      );
    } else if (viewingMode === 'editing' && itemViewingMode === 'editing' && !hideOverflowMenuOptions) {
      return (
        <div className={styles.actionButtons}>
          <IconButton
            onClick={() => {
              changeLocalbusUnitViewingMode(item.index, 'viewing');
              resetLocalbusUnitFormValues(item.index);
            }}
            className={`${styles.button} ${styles.cancelButton}`}
          >
            <CloseRoundedIcon sx={{ fontSize: '16px' }} />
          </IconButton>
          <IconButton
            disabled={!!fieldError}
            onClick={() => {
              setLocalbusUnitLocationValue(item.index);
              setLocalbusUnitEventsValue(item.index);
              changeLocalbusUnitViewingMode(item.index, 'viewing');
            }}
            className={`${styles.button} ${styles.checkButton} ${fieldError ? styles.disabledIcon : ''}`}
          >
            <CheckRoundedIcon />
          </IconButton>
        </div>
      );
    } else {
      return <></>;
    }
  };

  const monitoringIcon = (item: IS32LocalBusUnit) => {
    const deviceAlerts = getDeviceAlertsForLocalbusUnit(selectedIS32ConfigurationUnit?.address ?? '', item.reference);

    if (deviceAlerts.length === 1) {
      return (
        <Tooltip
          title={
            <>
              {deviceAlerts.map((alert) => (
                <div key={`alert-${alert.type}-${alert.reference}`}>
                  {t(`monitoring.${alert.type}`, {
                    address: selectedIS32ConfigurationUnit?.address,
                    location: alert.location,
                  })}
                </div>
              ))}
            </>
          }
        >
          <WarningAmberRounded className={styles.malfunctionIcon} />
        </Tooltip>
      );
    }
  };

  const renderLastColumn = (item: IS32LocalBusUnit) => {
    return (
      <div className={styles.lastColumn}>
        {monitoringIcon(item)}
        {actionButtons(item)}
      </div>
    );
  };

  const columns: ColumnDefinition<IS32LocalBusUnit, NestedKeyof<IS32LocalBusUnit>>[] = [
    {
      key: 'displayType',
      label: t('configuration.localbusUnitsConfiguration.type'),
      disableSort: true,
      renderCustomContentProvider: (item) => (
        <Typography
          variant="subtitle1"
          sx={{
            textDecoration: localbusUnitIsDeleted(item.reference) ? 'line-through' : 'none',
          }}
        >
          {item.displayType}
        </Typography>
      ),
      tableCellProps: (item) => ({
        sx: {
          verticalAlign: item.type === IS32LocalbusUnitType.OP ? 'top' : undefined,
        },
      }),
    },
    {
      key: 'serial',
      label: t('configuration.localbusUnitsConfiguration.serial'),
      disableSort: true,
      renderCustomContentProvider: (item) => (
        <Typography
          variant="subtitle1"
          sx={{
            textDecoration: localbusUnitIsDeleted(item.reference) ? 'line-through' : 'none',
          }}
        >
          {item.serial}
        </Typography>
      ),
      tableCellProps: (item) => ({
        sx: {
          verticalAlign: item.type === IS32LocalbusUnitType.OP ? 'top' : undefined,
        },
      }),
    },
    {
      key: 'locations',
      label: t('configuration.localbusUnitsConfiguration.location'),
      disableSort: true,
      renderCustomContentProvider: (item) => selectLocation(item),
      tableCellProps: (item) => ({
        width: viewingMode === 'editing' ? '300px' : '160px',
        sx: {
          verticalAlign: item.type === IS32LocalbusUnitType.OP ? 'top' : undefined,
        },
      }),
    },
    {
      key: 'reference',
      label: t('configuration.localbusUnitsConfiguration.events'),
      disableSort: true,
      renderCustomContentProvider: (item) => localbusUnitEvents(item),
    },
    {
      key: 'type',
      label: '',
      disableSort: true,
      renderCustomContentProvider: (item) => renderLastColumn(item),
      tableCellProps: {
        width: viewingMode === 'editing' ? '60px' : '0px',
      },
    },
  ];

  const details = () => (
    <div className={styles.contentContainer}>
      <Table
        className={styles.heading}
        data={
          selectedIS32ConfigurationUnit?.localBusUnits?.map<RowItem<IS32LocalBusUnit>>((localbusUnit, index) => ({
            showColorOnHover: false,
            data: {
              reference: localbusUnit.reference,
              serial: localbusUnit.serial,
              type: localbusUnit.type,
              displayType: localbusUnit.displayType,
              locations: localbusUnit.locations,
              index: index,
              events: localbusUnit.events,
            },
          })) ?? []
        }
        columns={columns}
        rowIdentifier={(item: IS32LocalBusUnit) => item.serial?.toLocaleString() ?? ''}
      />
    </div>
  );

  return (
    <>
      {accordionEnabled ? (
        <IS32ConfigurationAccordion
          header={header()}
          details={details()}
          isExpanded={localbusUnitsExpanded}
          handleAccordionExpandedChange={(expanded) => handleAccordionExpandedChange(IS32AccordionType.LocalbusUnits, expanded)}
        />
      ) : (
        <>
          {header()}
          {details()}
        </>
      )}
      {clickedRow && (
        <OverflowMenu
          isOpen={isOverflowMenuOpen}
          onClose={closeOverflowMenu}
          anchorEl={anchorEl}
          menuItems={getOverflowMenuItems(clickedRow, clickedPOEntryIndex)}
        />
      )}
    </>
  );
};

export default IS32LocalbusUnits;
